import '../../OrganizationTables.scss';
import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@trackman/web-shared-components';

import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { useState } from 'react';
import { Student } from 'types/organization/students';
import { DeleteStudents } from '../DeleteStudents/DeleteStudents';
import { handleSearchStudent } from 'utilities/search-helper';
import { OrganizationTableInitials } from '../../OrganizationTableInitials/OrganizationTableInitials';
import { NoEntriesMatching } from '../../../../../components/NoEntriesMatching/NoEntriesMatching';

type Props = {
  studentList: Student[];
  filter: string;
};

type StudentsTableEntry = {
  id: string;
  name: string;
  classNames: string | null;
  email: string;
};

export const StudentList = ({ studentList, filter }: Props) => {
  const { t } = useTranslation();
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [isDeleteStudentsModalOn, setIsDeleteStudentsModalOn] = useState<boolean>(false);
  const [studentIdUnderAction, setStudentIdUnderAction] = useState<string>('');

  const allTableEntries: StudentsTableEntry[] = studentList.map((student) => ({
    id: student.id,
    name: student.name,
    email: student.email,
    classNames: student?.classes?.map((c) => c.name).join(', ') ?? null,
  }));

  const filteredTableEntries = handleSearchStudent(filter, allTableEntries);

  const pageTableEntries = filteredTableEntries.slice(page * entriesPerPage, page * entriesPerPage + entriesPerPage);

  const handleSelectAll = () => {
    if (selectedStudentIds.length === studentList.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(studentList.map((student) => student.id));
    }
  };

  const handleSelectEntry = (id: string) => {
    if (selectedStudentIds.includes(id)) {
      setSelectedStudentIds(selectedStudentIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedStudentIds([...selectedStudentIds, id]);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenRemoveStudentDialog = (id: string) => {
    setStudentIdUnderAction(id);
    setIsDeleteStudentsModalOn(true);
  };

  const handleDeleteStudents = () => {
    setIsDeleteStudentsModalOn(false);
    if (studentIdUnderAction) {
      setStudentIdUnderAction('');
    } else {
      setSelectedStudentIds([]);
    }
  };

  return (
    <div className='organization-table-card-wrapper student-list'>
      <div className='organization-table-header'>
        <strong>{t('Students', { ns: 'studentsPage', number: filteredTableEntries.length })}</strong>
        <Button
          startIcon={<TrashIcon />}
          label={t('DeleteStudents', { ns: 'studentsPage' })}
          className='organization-button'
          onClick={() => setIsDeleteStudentsModalOn(true)}
          intent='secondary'
          disabled={selectedStudentIds.length === 0}
        />
      </div>
      {filteredTableEntries.length > 0 ? (
        <Table sx={{ minWidth: 750 }} aria-labelledby='studentsTable' className='tmu-table'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'>
                <Checkbox
                  color='primary'
                  indeterminate={selectedStudentIds.length > 0 && selectedStudentIds.length < allTableEntries.length}
                  checked={allTableEntries.length > 0 && selectedStudentIds.length === allTableEntries.length}
                  onChange={() => handleSelectAll()}
                  label={undefined}
                />
              </TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
              <TableCell sx={{ width: 250 }}>{t('Name', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 250 }}>{t('Classes', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 250 }}>{t('Email', { ns: 'studentsPage' })}</TableCell>
              <TableCell sx={{ width: 50 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageTableEntries.map((entry) => (
              <TableEntry
                key={entry.id}
                isItemSelected={selectedStudentIds.includes(entry.id)}
                tableEntry={entry}
                handleSelectEntry={() => handleSelectEntry(entry.id)}
                openRemoveEntryDialog={() => handleOpenRemoveStudentDialog(entry.id)}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={allTableEntries.length}
                rowsPerPage={entriesPerPage}
                page={page}
                onPageChange={(e, newPage: number) => setPage(newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      ) : (
        <NoEntriesMatching />
      )}
      {isDeleteStudentsModalOn && (
        <DeleteStudents
          isOpen={isDeleteStudentsModalOn}
          studentIds={studentIdUnderAction ? [studentIdUnderAction] : selectedStudentIds}
          close={handleDeleteStudents}
        />
      )}
    </div>
  );
};

const TableEntry = ({ isItemSelected, tableEntry, handleSelectEntry, openRemoveEntryDialog }) => {
  const selectEntry = (event) => {
    event.stopPropagation();
    handleSelectEntry();
  };

  const handleRemoveEntry = (event) => {
    event.stopPropagation();
    openRemoveEntryDialog();
  };
  return (
    <TableRow
      hover
      onClick={(event) => selectEntry(event)}
      role='checkbox'
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={tableEntry.id}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell padding='checkbox'>
        <Checkbox color='primary' checked={isItemSelected} label={undefined} />
      </TableCell>
      <TableCell className='initials-cell'>
        <OrganizationTableInitials name={tableEntry.name} />
      </TableCell>
      <TableCell>{tableEntry.name}</TableCell>
      <TableCell>{tableEntry.classNames}</TableCell>
      <TableCell>{tableEntry.email}</TableCell>
      <TableCell>
        <div className='buttons-cell-content'>
          <IconButton icon={() => <TrashIcon />} onClick={handleRemoveEntry} />
        </div>
      </TableCell>
    </TableRow>
  );
};
